import {
    events,
    RunConfigService,
    ConfigService,
    SponsorStorageService,
    MarketSiteRegionsV2,
    TdcStrings,
    ShoppingContext,
    StoreFrontSponsorStorageService,
    PersonalOfferStorageService
} from "@nuskin/ns-util";
import { UserService } from '@nuskin/ns-account'
import { isNullOrEmpty, getProp } from '@nuskin/ns-common-lib'
import { MySiteBanner, FeaturedProduct, MySiteBundle } from './mySiteDtos'
import axios from 'axios'
import { MySiteError } from './mySiteError'
import { ImageType } from './ImageType'

let user
let sponsor
let subdomain
let marketBanners = {}
let marketProducts = {}

const DB_STORAGE_ORIGIN = 'storage.googleapis.com';
const API_PATH = getAPIPath();
const http = axios.create();

/**
 * Service facilitating communication with Firebase for My Site and Personal Offer data.
 *
 * In Firebase, a given My Site subdomain is tied to the distributor ID of its sponsor. This also applies to subdomains that have been imported from the Nu Skin OPS program.
 * Elsewhere in Firebase, sponsor records are maintained for distributors with My Site stores and/or Personal Offers. These records include profile information and My Site store settings.
 * This service provides read/write access to this data as it pertains to My Site.
 * @namespace MySiteRestService
 */
export default {
    initializeStaticApp,
    getCurrentSubdomain,
    getCurrentSponsorId,
    getSponsorData,
    getMarketFeaturedProducts,
    getMarketFeaturedBundle,
    getMarketMySiteConfig,
    isSubdomainBlacklisted,
    isSubdomainAvailable,
    getMarketBanners,
    saveMarketBanners,
    initialSponsorSetup,
    removeSubdomain,
    updateSponsorData,
    changeSubdomain,
    saveFullProductCatalog,
    setSocialMediaAccounts,
    uploadImage,
    removeImage,
    saveFeaturedProducts,
    saveBanner,
    saveBundles,
    touchLanguage,
    updateMarketAndLanguageSpecificData,
    getEnabledMarketsAndLanguages,
    saveMySiteLogos,
    savePriceType,
    reset,
    getMySiteNav,
    saveSubdomain
}

//=================================================================//
//  PUBLIC FUNCTIONS
//=================================================================//

/**
 * Initializes and returns the Mysite configuration for a static app (created for Catalog). Don't use with AEM.
 * @returns {{subdomain: String, sponsorId: String, sponsor: Object}}
 * @memberof MySiteRestService
 * @public
 */
async function initializeStaticApp(env) {
    const runConfig = RunConfigService.getRunConfig();
    if (
        isNullOrEmpty(runConfig) ||
        !runConfig.baseUrl ||
        !runConfig.country ||
        !runConfig.language
    ) {
        console.error("RunConfig not set/available yet.");
        return;
    }

    if (!env) {
        env = RunConfigService.getEnvironmentCode();
    }

    const hostRegex = /(?<store>\w+)\.((?<env>test)\.)?mynuskin\.com/gi;
    const hostGroups =
        (hostRegex.exec(window.location.hostname) || {}).groups || {};
    const mysiteHost = {
        store: (hostGroups.store === "mysite" ? "" : hostGroups.store) || "",
        env: hostGroups.env || env || ""
    };

    const currentUrl = new URL(window.location.href);
    const mysiteQueries = {
        store: currentUrl.searchParams.get("store") || "",
        storeId:
            currentUrl.searchParams.get("storeId") ||
            currentUrl.searchParams.get("userId") ||
            "",
        offerId:
            currentUrl.searchParams.get("offerId") ||
            currentUrl.searchParams.get("pitchId") ||
            ""
    };

    sponsor = sponsor || StoreFrontSponsorStorageService.getStoreFrontSponsor();
    let sponsorSet = !isNullOrEmpty(sponsor);
    const sponsorSubdomain = sponsorSet
        ? Object.keys(sponsor.storefront || {})[0] || ""
        : "";
    const sponsorSapId = sponsorSet ? sponsor.sapId || "" : "";

    subdomain =
        mysiteHost.store ||
        mysiteQueries.store ||
        sponsorSubdomain ||
        subdomain ||
        "";
    let sponsorId =
        mysiteQueries.storeId ||
        sponsorSapId ||
        SponsorStorageService.getSponsor() ||
        "";

    if (subdomain && (!sponsorId || !sponsorSet || sponsorSubdomain !== subdomain)) {
        try {
            const newSponsorId = await getSponsorIdBySubdomain(subdomain);
            if (newSponsorId && newSponsorId != sponsorId) {
                sponsorId = newSponsorId;

                SponsorStorageService.setSponsor(sponsorId);
            }
        } catch (error) {
            // do nothing
        }
    }

    if (sponsorId && (!sponsorSet || sponsorSapId !== sponsorId)) {
        try {
            // getSponsorData sets sponsor and subdomain
            await getSponsorData(sponsorId, true);
            sponsorSet = !isNullOrEmpty(sponsor);
            if (sponsorSet) {
                StoreFrontSponsorStorageService.setStoreFrontSponsor(sponsor);
                events.publish(events.shop.SPONSOR_SET);
            }
        } catch (error) {
            // do nothing
        }
    }

    if (sponsorSet) {
        const personalOffer = PersonalOfferStorageService.getPersonalOffer();
        const personalOfferId =
            mysiteQueries.offerId || (personalOffer || {}).pitchID;

        if (
            personalOfferId &&
            !ShoppingContext.isContext(ShoppingContext.PERSONAL_OFFER)
        ) {
            ShoppingContext.setShoppingContext(ShoppingContext.PERSONAL_OFFER);
        } else if (
            !personalOfferId &&
            !ShoppingContext.isContext(ShoppingContext.PERSONAL_OFFER)
        ) {
            const market = runConfig.country;
            const language = runConfig.language;
            const isStorefrontWholesale = !!getProp(
                sponsor,
                `storefront.${subdomain}.${market}.lang.${language}.wholesale`,
                false
            );

            ShoppingContext.setShoppingContext(ShoppingContext.STOREFRONT, {
                showWholeSalePricing: isStorefrontWholesale,
                allowAnonymousShopping: !isStorefrontWholesale,
                preferredSignupOnly: isStorefrontWholesale
            });
        }

        let searchParamsChanged = false;
        if (
            !personalOfferId &&
            !mysiteHost.store &&
            !mysiteQueries.store &&
            (!mysiteQueries.storeId || mysiteQueries.storeId !== sponsorId)
        ) {
            // prefer the subdomain (store) over user ids
            currentUrl.searchParams.set("store", subdomain);
            currentUrl.searchParams.delete("storeId");
            currentUrl.searchParams.delete("userId");
            searchParamsChanged = true;
        }

        if (searchParamsChanged) {
            attemptRedirect(currentUrl.href);
        }
    } else {
        if (SponsorStorageService.getSponsor()) {
            PersonalOfferStorageService.clearPersonalOffer();
            StoreFrontSponsorStorageService.clearStoreFrontSponsor();
            SponsorStorageService.clearSponsor();
        }

        if (
            ShoppingContext.isContext(ShoppingContext.STOREFRONT) ||
            ShoppingContext.isContext(ShoppingContext.PERSONAL_OFFER)
        ) {
            StoreFrontSponsorStorageService.clearStoreFrontSponsor();
        }

        if (
            mysiteHost.store ||
            mysiteQueries.store ||
            mysiteQueries.storeId ||
            mysiteQueries.offerId
        ) {
            const redirectHost = window.location.hostname.endsWith("mynuskin.com")
                ? ConfigService.getMarketConfig().siteUrl
                : window.location.origin;
            currentUrl.searchParams.delete("store");
            currentUrl.searchParams.delete("storeId");
            currentUrl.searchParams.delete("userId");
            currentUrl.searchParams.delete("offerId");
            currentUrl.searchParams.delete("pitchId");
            const redirectUrl = `${redirectHost}${window.location.pathname}${currentUrl.search}`;
            attemptRedirect(redirectUrl);
        }

        subdomain = "";
        sponsorId = "";
        sponsor = null;
    }

    return {
        subdomain,
        sponsorId,
        sponsor
    };
}

/**
 * Returns the subdomain of the My Site store the current page is on. Returns null if no My Site subdomain can be found.
 * @returns {String} Subdomain name
 * @memberof MySiteRestService
 * @public
 */
async function getCurrentSubdomain() {
    if (subdomain) return subdomain
    // get from url
    if (!subdomain) {
        let path = window.location.pathname.split('.mysite.')[1]
        subdomain = path ? path.replace(/\.html/, '') : ''
    }

    // get from sponsor object
    if (!subdomain && sponsor && sponsor.storefront) {
        subdomain = Object.keys(sponsor.storefront)[0]
        return subdomain
    }
    // get sponsor data and subdomain
    //checking if storefront variable is present
    if (!subdomain || !StoreFrontSponsorStorageService.getStoreFrontSponsor()) {
        let id = await getCurrentSponsorId()
        await getSponsorData(id)
    }
    return subdomain
}

/**
 * Retrieves the ID of the sponsor for the My Site store the current page is on.
 * Attempts to retrieve this value first from the url, then from local storage, and then finally from Firebase through a query for the current subdomain.
 * Resolves to null if no sponsor ID can be found.
 * @returns {Promise.<String>} Promise that resolves to sponsor ID or null if none found
 * @memberof MySiteRestService
 * @public
 */
async function getCurrentSponsorId() {
    const searchParams = new URLSearchParams(window.location.search);
    let sponsorId =
        searchParams.get("storeId") ||
        searchParams.get("userId") ||
        (sponsor || {}).sapId ||
        SponsorStorageService.getSponsor() ||
        "";

    if(sponsorId) {
        sponsorId = sponsorId.replace(/[.&].*/g, '');
    }

    if (!sponsorId) {
        user = UserService.getUser();
        sponsorId = user ? user.id : "";
    }

    if (!sponsorId) {
        subdomain = await getCurrentSubdomain();
        if (subdomain) {
            sponsorId = await getSponsorIdBySubdomain(subdomain);
        }
    }

    if (!sponsorId) {
        throw new Error('Missing sponsor');
    }

    let urlSponsor = `${API_PATH}/v1/my-site/sponsor/${sponsorId}`
    let xhrSponsor = new XMLHttpRequest();
    xhrSponsor.responseType = 'json';
    xhrSponsor.open('GET', urlSponsor, true)
    xhrSponsor.setRequestHeader('client_id', '735b1eb810304bba966af0891ab54053')
    xhrSponsor.onload = function () {
        if (xhrSponsor.readyState === 4 && xhrSponsor.status === 200) {
            StoreFrontSponsorStorageService.setStoreFrontSponsor((xhrSponsor.response || {}).sponsor || {});
        }
    };
    xhrSponsor.send(null)

    if (
        ShoppingContext.isContext(ShoppingContext.PERSONAL_OFFER) ||
        ShoppingContext.isContext(ShoppingContext.STOREFRONT)
    ) {
        SponsorStorageService.setSponsor(sponsorId);
    }

    return sponsorId;
}

/**
 * Returns the My Site sponsor record for the given distributor ID (or for the current user if no ID is provided).
 * Returns null if no sponsor record is found.
 * @param {String} [id] - My Site sponsor's distributor ID required if not retrieving data of current authenticated user
 * @param {Boolean} [forceRefresh=false] - Flag indicating whether to force a request for fresh data from Firebase
 * @returns {Promise.<>} Promise that resolves to {@link } object or null if no data is available
 * @memberof MySiteRestService
 * @public
 */
async function getSponsorData(id, forceRefresh) {
    id = id || fallbackToCurrentUserId()

    // Return cached data if available and not forcing a refresh
    if (sponsor && !forceRefresh) return Object.assign({}, sponsor)

    const url = `${API_PATH}/v1/my-site/sponsor/${id}`
    let res = await mySiteHttp({ url })
    sponsor = res.sponsor
    subdomain = sponsor.storefront ? Object.keys(sponsor.storefront)[0] : ''
    return sponsor
}

/**
 * Returns a market's list of featured products.
 * @param {Object} [options] - optional parameters:
 * @param {String} [options.country] - Country code defaults to current country
 * @param {String} [options.language] - Language code defaults to current language
 * @returns {Promise.<Array<FeaturedProduct>>} Promise that resolves to list of products
 * @memberof MySiteRestService
 * @public
 * @see FeaturedProduct
 */
async function getMarketFeaturedProducts(options) {
    let data = await getMarketFeaturedProductsData(options)
    let products = data['products'] || []
    return products.map(p => new FeaturedProduct(p))
}

/**
 * Returns a market's featured product bundle.
 * @param {Object} [options] - optional parameters:
 * @param {String} [options.country] - Country code defaults to current country
 * @param {String} [options.language] - Language code defaults to current language
 * @returns {Promise.<Array<FeaturedProduct>>} Promise that resolves to list of products
 * @memberof MySiteRestService
 * @public
 * @see FeaturedProduct
 */
async function getMarketFeaturedBundle(options) {
    let data = await getMarketFeaturedProductsData(options)
    let bundle = data['bundles'] || []
    return bundle.map(p => p.sku)
}

/**
 * Checks the given subdomain against a blacklist.
 * Returns true if the subdomain contains blacklisted words, false otherwise.
 * @param {String} subdomain - My Site subdomain to check against blacklist
 * @returns {Promise.<Boolean>} Promise that resolves to boolean
 * @memberof MySiteRestService
 * @public
 */
async function isSubdomainBlacklisted(subdom) {
    let isBlacklisted
    const url = `${API_PATH}/v1/my-site/subdomain/${subdom}/blacklisted`
    let res = await mySiteHttp({ url })
    isBlacklisted = res.isBlacklisted
    return isBlacklisted
}

/**
 * Checks the given subdomain against existing subdomains, to determine whether it is already taken.
 * Returns true if the given subdomain is available (not already taken), false otherwise.
 * @param {String} subdomain - My Site subdomain to check
 * @returns {Promise.<Boolean>} Promise that resolves to boolean
 * @memberof MySiteRestService
 * @public
 */
async function isSubdomainAvailable(subdom) {
    const url = `${API_PATH}/v1/my-site/subdomain/${subdom}`
    let res = await mySiteHttp({ url })
    const id = res.subdomainOwner
    return !id || id === getUser().id
}

/**
 * Retrieves the current market's My Site banners from Firebase.
 * Returns an empty array if the market has no banners.
 * @returns {Promise<Array<MySiteBanner>>}
 * @memberof MySiteRestService
 * @public
 */
async function getMarketBanners(ctry, lng) {
    let cfg = RunConfigService.getRunConfig()
    ctry = ctry || cfg.country
    lng = lng || cfg.language

    let mkt = `${lng}_${ctry}`
    if (marketBanners[mkt]) return marketBanners[mkt]

    let url = `${API_PATH}/v1/my-site/market/${mkt}/banners`
    let res = await mySiteHttp({ url })
    let data = res.banners
    let banners = (data && (data instanceof Array)) ? data.map(b => new MySiteBanner(b)) : []
    marketBanners[mkt] = banners
    return banners
}

/**
 * Overwrites the current market's My Site banners with the given data.
 * Returns the updated banners list.
 * @param {Array<MySiteBanner>} banners
 * @returns {Promise<Array<MySiteBanner>>}
 * @memberof MySiteRestService
 * @public
 */
async function saveMarketBanners(banners) {
    let cfg = RunConfigService.getRunConfig()
    let mkt = `${cfg.language}_${cfg.country}`
    let url = `${API_PATH}/v1/my-site/market/${mkt}/banners`
    await mySiteHttp({
        url,
        method: 'put',
        data: { banners }
    })
    let res = await mySiteHttp({ url })
    let updatedBanners = res.banners
    return updatedBanners
}

async function getMarketMySiteConfig(country, language) {
    let result = {}
    let runCfg = RunConfigService.getRunConfig()
    let url = runCfg.baseUrl + '/content/nuskin/' + language + '_' + country + '/tools/mysite.infinity.3.json'
    let data = await mySiteHttp({ url })
    if (data && data['jcr:content'] && data['jcr:content'].bodyContent) {
        Object.keys(data['jcr:content'].bodyContent).forEach(key => {
            if (key.indexOf('mysitewrapper') !== -1) result = data['jcr:content'].bodyContent[key]
        })
    }
    return result
}

async function getMySiteNav(country, language) {
    let result = []
    let runCfg = RunConfigService.getRunConfig()
    let url = `${runCfg.baseUrl}/content/nuskin/${language}_${country}/jcr:content/nsHeaderStoreFront/mySiteNavigation.json`
    let data = await mySiteHttp({ url })
    for (let i = 0; i < data.mySiteNavigation.length; i++) {
        let path = data.mySiteNavigation[i].replace(/\{.+":"|"\}/g, '')
        url = `${runCfg.baseUrl}${path}/jcr:content/jcr:title.json`
        let res = await mySiteHttp({ url })
        result.push(res['jcr:title'])
    }
    return result
}

/**
 * Sets up the current user as a My Site sponsor for the given subdomain and the current country in the current language
 * @param {String} subdomain - My Site subdomain to initialize
 * @param {} data - My Site sponsor data
 * @param {String} storeName - My Site store name
 * @param {String} oldSubdomain - Existing subdomain assigned to user ID
 * @param {String} country - Country
 * @param {String} language - Language within the market
 * @param {Boolean} defaultLang - Identifies whether language needs to be set as default
 * @returns {Promise.<>} Promise that resolves to updated sponsor record
 */
async function initialSponsorSetup(subdom, data, storeName, oldSubdomain, country, language, defaultLang, fullProductCatalog) {
    let cfg = RunConfigService.getRunConfig()
    country = country || cfg.country
    language = language || cfg.language

    let featuredProducts = await getMarketFeaturedProducts({ country, language })

    // Create My Site store data in sponsor record and prefill with default values
    data.storefront = data.storefront || {}
    data.storefront[subdom] = data.storefront[subdom] || {}
    data.storefront[subdom][country] = data.storefront[subdom][country] || {}
    data.storefront[subdom][country].products = data.storefront[subdom][country].products || featuredProducts.map(item => item.sku)

    // GET DEFAULTS FROM AEM
    let defaultBundle = await getMarketFeaturedBundle({ country })
    let defaultBundleId = 0 //default bundle id is the same across markets/languages

    data.storefront[subdom][country].bundles = data.storefront[subdom][country].bundles || [
        {
            id: defaultBundleId,
            skus: defaultBundle
        }
    ]

    let marketBnrs = await getMarketBanners(country, language)
    let defaultBanner = marketBnrs[0] // default banner is indexed 0

    let mySiteTranslationsTdc = await TdcStrings.getStrings('mysite/mysite-home', language, country) || {}
    let mySiteTranslationsNonTdc = await TdcStrings.getNonTdcStrings('mysite/mysite-home', language, country) || {}
    if (!Object.keys(mySiteTranslationsTdc).length)
        throw new Error(`My Site Home Page was not setup for ${language}_${country} in AEM`)

    data.useDefaultLogos = data.useDefaultLogos !== undefined ? data.useDefaultLogos : true
    data.profilePhoto = data.profilePhoto || mySiteTranslationsNonTdc.welcomeSectionProfileImageReference
    data.siteLogo = data.siteLogo || mySiteTranslationsNonTdc.welcomeSectionLogoImageReference

    data.storefront[subdom][country].showDefaultBundles = data.storefront[subdom][country].showDefaultBundles !== undefined ? data.storefront[subdom][country].showDefaultBundles : true
    data.storefront[subdom][country].showDefaultProducts = data.storefront[subdom][country].showDefaultProducts !== undefined ? data.storefront[subdom][country].showDefaultProducts : true

    data.storefront[subdom][country].lang = data.storefront[subdom][country].lang || {}

    let runCfg = RunConfigService.getRunConfig()
    let url = runCfg.baseUrl + '/content/nuskin/' + language + '_' + country + '/tools/mysite/jcr:content/bodyContent/mysitewrapper_222c.json'
    let config = await mySiteHttp({ url })

    fullProductCatalog = fullProductCatalog === undefined ? (config.fullProductCatalogFormShowSearch && config.fullProductCatalogFormShowSearch === 'true') : false
    let wholesale = config.priceTypeWholesaleDefault && config.priceTypeWholesaleDefault === 'true'

    data.storefront[subdom][country].lang[language] = data.storefront[subdom][country].lang[language] || {
        default: !!defaultLang,
        fullProductCatalog: !!fullProductCatalog,
        wholesale,
        storeName: storeName || mySiteTranslationsTdc.welcomeSectionSiteName || '',
        aboutYourStore: mySiteTranslationsTdc.welcomeSectionAboutYourSite || '',
        purchaseMessage: mySiteTranslationsTdc.welcomeSectionPurchase || '',
        aboutProducts: mySiteTranslationsTdc.featuredProductsDescription || '',
        banner: defaultBanner || '',
        bundlesInfo: [
            {
                default: !!defaultLang,
                bundle_id: defaultBundleId,
                name: mySiteTranslationsTdc.featuredBundleTitle || '',
                about: mySiteTranslationsTdc.defaultBundlesMessage || ''
            }
        ]
    }
    //remove old OPS subdomain if user chose not to use it for mysite
    if (oldSubdomain && oldSubdomain !== subdom) removeSubdomain(oldSubdomain)
    subdomain = subdom
    // Save sponsor record
    return updateSponsorData(data)
}


/**
 * Removes subdomain from firebase
 * @param {*} subdom - subdomain to remove
 */
async function removeSubdomain(subdom) {
    let url = `${API_PATH}/v1/my-site/subdomain/${subdom}`
    return mySiteHttp({
        url,
        method: 'delete'
    })
}
/**
 * Saves subdomain to firebase
 * @param {*} subdom - subdomain to remove
 */
async function saveSubdomain(subdom) {
    let url = `${API_PATH}/v1/my-site/subdomain/${subdom}`
    return mySiteHttp({
        url,
        method: 'post'
    })
}

/**
 * Updates the current user's My Site sponsor record in Firebase with the given data.
 * Returns the updated sponsor record.
 * @param {} spnsr - Sponsor record data to save
 * @returns {Promise.<>} Promise that resolves to updated sponsor record
 * @throws {Error} if the current user is unauthenticated
 * @memberof MySiteRestService
 * @public
 */
async function updateSponsorData(spnsr) {
    let url = `${API_PATH}/v1/my-site/sponsor`
    let res = await mySiteHttp({
        url,
        method: 'put',
        data: spnsr
    })
    return updateCachedSponsorData(res.sponsor)
}

/**
 * Changes Distributors storefront subdomain
 * @param {String} subdomain - new desired subdomain
 */
async function changeSubdomain(subdom) {
    let url = `${API_PATH}/v1/my-site/subdomain/${encodeURIComponent(subdom)}`
    let res = await mySiteHttp({
        url,
        method: 'put'
    })
    subdomain = subdom
    return updateCachedSponsorData(res.sponsor)
}

/**
 * @param {Boolean} value
 * @memberof MySiteRestService
 * @returns {Promise.<>} Promise that resolves to updated sponsor record
 * @public
 */
async function saveFullProductCatalog(value, country, language) {
    let update = {
        [`/storefront/${subdomain}/${country}/lang/${language}/fullProductCatalog`]: !!value
    }
    return updateSponsorData(update)
}

/**
 * @param {Boolean} value
 * @memberof MySiteRestService
 * @returns {Promise.<>} Promise that resolves to updated sponsor record
 * @public
 */
async function savePriceType(value, country, language) {
    let update = {
        [`/storefront/${subdomain}/${country}/lang/${language}/wholesale`]: !!value
    }
    return updateSponsorData(update)
}

/**
 * Sets the given {@link MySiteSocialAccount}s as the social media accounts for the current user's My Site store.
 * @param {Array<MySiteSocialAccount>} accounts - List of social media account details
 * @returns {Promise.<>} Promise that resolves to updated My Site sponsor record
 */
async function setSocialMediaAccounts(accounts) {
    let update = {
        socialAccounts: toCleanedObj(accounts)
    }
    return updateSponsorData(update)
}

/**
 * Saves the given image to Firebase and returns the image's download url.
 * This method is intended for use with My Site sponsor profile images.
 * @param {File|String} file - image file object or a base64 string
 * @param {ImageType} type - image type
 * @returns {Promise<String>} Promise that resolves to image's download url
 */
async function uploadImage(file, type, noAuth) {
    //convert to base64
    let url = `${API_PATH}/v1/my-site/image${noAuth ? '/no-auth' : ''}`
    if (file instanceof Blob) {
        file = await new Promise((resolve) => {
            let reader = new FileReader();
            reader.onloadend = () => {
                resolve(reader.result)
            }
            reader.readAsDataURL(file);
        })
    }

    let cfg = RunConfigService.getRunConfig()

    let res = await mySiteHttp({
        url,
        method: 'put',
        data: { file, type, market: cfg.country, language: cfg.language }
    })
    return res.imgUrl
}

/**
 * Removes the image corresponding to the given download url from Firebase.
 * This method is intended for use with My Site sponsor profile images.
 * @param {String} imgUrl - image url
 * @returns {Promise.<void>} Promise that resolves if deletion was successful.
 */
async function removeImage(imgUrl) {
    let url = `${API_PATH}/v1/my-site/image/${encodeURIComponent(imgUrl)}`
    return mySiteHttp({
        url,
        method: 'delete'
    })
}


async function saveMySiteLogos(profilePhoto, siteLogo, useDefaultLogos) {
    if (!useDefaultLogos) {
        if (profilePhoto.includes('base64')) {
            if (sponsor.profilePhoto.includes(DB_STORAGE_ORIGIN)) await removeImage(sponsor.profilePhoto)
            profilePhoto = await uploadImage(profilePhoto, ImageType.PROFILE)
        }
        if (siteLogo.includes('base64')) {
            if (sponsor.siteLogo.includes(DB_STORAGE_ORIGIN)) await removeImage(sponsor.siteLogo)
            siteLogo = await uploadImage(siteLogo, ImageType.SITE_LOGO)
        }
    }

    let data = {
        profilePhoto,
        siteLogo,
        useDefaultLogos
    }

    return updateSponsorData(data)
}

/**
 * Saves skus of featured products
 * @param {Array<String>} skus - list of products skus.
 * @param {String} aboutProducts - featured products description
 * @returns {Promise}
 */
async function saveFeaturedProducts(skus, aboutProducts, ctry, lng, showDefaultProducts) {

    let runCfg = RunConfigService.getRunConfig()
    let country = ctry || runCfg.country
    let language = lng || runCfg.language

    let update = {
        [`/storefront/${subdomain}/${country}/showDefaultProducts`]: showDefaultProducts,
        [`/storefront/${subdomain}/${country}/products`]: skus,
        [`/storefront/${subdomain}/${country}/lang/${language}/aboutProducts`]: aboutProducts
    }
    return updateSponsorData(update)
}

async function updateMarketAndLanguageSpecificData(country, language, data) {
    let update = {}
    let path = `/storefront/${subdomain}/${country}/lang/${language}/`
    Object.keys(data).forEach(key => {
        update[path + key] = data[key]
    })
    return updateSponsorData(update)
}

/**
 * Saves selected banner
 * @param {Object} banner - banner object
 * @returns {<Promise>}
 */
async function saveBanner(banner) {

    let runCfg = RunConfigService.getRunConfig()
    let country = runCfg.country
    let language = runCfg.language

    let update = {
        [`/storefront/${subdomain}/${country}/lang/${language}/banner`]: banner
    }
    return updateSponsorData(update)
}

/**
 * Saves bundles
 * @param {Array<Object>} bundles - bundles list
 * @returns {<Promise>}
 */
async function saveBundles(bundles, bundlesInfo, country, language, showDefaultBundles) {

    let data = await getSponsorData()
    let update = {}

    if (bundles) {
        let currentBundles = data.storefront[subdomain][country].bundles || []

        // check if some bundle(s) is/are being deleted. If so, remove translations for those/this bundle(s) for the whole market.
        currentBundles.forEach(async bundle => {
            let match = bundles.find(item => item.id === bundle.id)
            if (!match) {
                let bundleIdToRemove = bundle.id
                let languages = data.storefront[subdomain][country].lang

                for (let i = 0; i < Object.keys(languages).length; i++) {
                    let lang = Object.keys(languages)[i]
                    if (languages[lang].bundlesInfo && languages[lang].bundlesInfo.length) {
                        let filteredBundlesInfo = languages[lang].bundlesInfo.filter(bi => bi.bundle_id !== bundleIdToRemove)
                        update[`/storefront/${subdomain}/${country}/lang/${lang}/bundlesInfo`] = filteredBundlesInfo
                    }
                }
            }
        })

        bundles = bundles.map(b => new MySiteBundle(b)) || []
        // filter out unintended bad data from bundles
        bundles = bundles.filter(b => b)
        bundles.forEach(b => b.skus = b.skus.filter(sku => sku))
        update[`/storefront/${subdomain}/${country}/bundles`] = bundles

    }

    if (bundlesInfo) {
        let languages = data.storefront[subdomain][country].lang || {}
        let remainingExistingLanguages = Object.keys(languages).filter(l => l !== language)

        // if new bundle(s) was/were added, then mark bundle(s) translations as default for the current market
        bundlesInfo.forEach(bInfo => {
            if (bInfo) {
                let match
                remainingExistingLanguages.forEach(lang => {
                    if (languages[lang].bundlesInfo)
                        languages[lang].bundlesInfo.forEach(bi => {
                            if (bi) {
                                if (bi.bundle_id === bInfo.bundle_id) match = true
                            }
                        })
                })

                if (!match) bInfo.default = true
            }
        })

        update[`/storefront/${subdomain}/${country}/lang/${language}/bundlesInfo`] = bundlesInfo
    }

    update[`/storefront/${subdomain}/${country}/showDefaultBundles`] = showDefaultBundles

    return updateSponsorData(update)
}

async function touchLanguage(country, language) {
    let update = {
        [`/storefront/${subdomain}/${country}/lang/${language}/lastEdited`]: new Date().toString()
    }
    return updateSponsorData(update)
}

async function getEnabledMarketsAndLanguages() {
    let runCfg = RunConfigService.getRunConfig()
    let url = runCfg.baseUrl + '/content/nuskin/storefront-redirect/jcr:content/storefrontMarkets.json'
    let data = await mySiteHttp({ url })
    let regCtryLang = await MarketSiteRegionsV2.getMarketSiteRegionData()
    let result = {}

    // make a map of key(country code) => value(list of language codes)
    data.storefrontMarkets.forEach(item => {
        let lang = item.split('_')[0]
        let ctry = item.split('_')[1]

        if (result[ctry])
            result[ctry].languages.push(lang)
        else
            result[ctry] = {
                languages: [lang]
            }
    })

    // get country/langauge names by their codes
    Object.keys(result).forEach(ctryCode => {
        let country
        let lng = []
        regCtryLang.find(region => {
            country = region.countryname.find(c => c.countryCode === ctryCode)
            return country
        })
        if (country) {
            result[ctryCode].displayName = country.locale_names[runCfg.language]
            result[ctryCode].defaultLocale = country.defaultLocale && country.defaultLocale.defaultLanguageCode
                ? country.defaultLocale
                : { defaultLanguageCode: country.languageCodes[0] }
            result[ctryCode].languages.forEach(language => {
                lng.push(
                    {
                        code: language,
                        displayName: country.displayLanguage[language]
                    }
                )
            })
            result[ctryCode].languages = lng
        }
    })
    return result
}


/**
 * Resets this service's cached data. This method is intended for use in
 * testing and is not recommended under normal use conditions.
 * @public
 */
function reset() {
    marketBanners = {}
    marketProducts = {}
}

//=================================================================//
//
//  PRIVATE FUNCTIONS
//
//=================================================================//

/**
 * Retrieves the ID of the sponsor for the My Site store subdomain requested.
 * Attempts to retrieve this value from Firebase through a query for the current subdomain.
 * Resolves to null if no sponsor ID can be found.
 * @returns {Promise.<String>} Promise that resolves to sponsor ID or null if none found
 * @memberof MySiteRestService
 * @private
 */
async function getSponsorIdBySubdomain(subdomain) {
    if (!subdomain) {
        throw new Error("Missing subdomain");
    }

    const url = `${API_PATH}/v1/my-site/subdomain/${subdomain}`;
    const res = await mySiteHttp({ url });

    return res.subdomainOwner;
}

/**
 * Attempts to redirect to a URL if different than the current URL.
 * @memberof MySiteRestService
 * @private
 */
function attemptRedirect(url) {
    if (url !== window.location.href) {
        window.location = url;
    }
}

/**
 * Returns the current user object.
 * @returns {Object}
 * @throws {Error} if the current user is unauthenticated.
 * @memberof MySiteRestService
 * @private
 */
function getUser() {
    user = user || UserService.getUser()
    if (!user)
        // eslint-disable-next-line no-undef
        nuskin.events.publish(nuskin.events.authentication.REQUEST_FOR_LOGIN)
    return user
}

/**
 * Returns the given distributor ID, or the distributor ID of the current user if no ID is provided.
 * @param {String} [id] - Distributor ID
 * @returns {String} distributor ID
 * @throws {Error} if no ID is provided AND the current user is unauthenticated
 * @memberof MySiteRestService
 * @private
 */
function fallbackToCurrentUserId(id) {
    if (!id) {
        let usr = getUser()
        if (!usr) {
            // eslint-disable-next-line no-undef
            nuskin.events.publish(nuskin.events.authentication.REQUEST_FOR_LOGIN)
            // throw new Error('MySiteRestService Error: sponsor ID must be provided or user must be authenticated')
        }
        id = usr.id
    }
    return id
}

/**
 * updates the cached My Site sponsor data of the current user from Firebase.
 * This function is called after edits are made to the user's My Site sponsor record.
 * @returns {Promise.<>} Promise that resolves to updated sponsor record
 * @memberof MySiteRestService
 * @private
 */
async function updateCachedSponsorData(data) {
    let id = await getCurrentSponsorId()
    data = data || await getSponsorData(id)
    sponsor = Object.assign({}, data)
    return sponsor
}

/**
 * Retrieves a market's featured products data.
 * @param {Object} [options] - optional parameters:
 * @param {String} [options.country] - Country code defaults to current country
 * @param {String} [options.language] - Language code defaults to current language
 * @returns {Promise.<Object>}
 * @memberof MySiteRestService
 * @private
 */
async function getMarketFeaturedProductsData(options) {
    let runCfg = RunConfigService.getRunConfig()

    const DEFAULT_OPTIONS = {
        country: runCfg.country,
        language: runCfg.language
    }
    options = Object.assign({}, DEFAULT_OPTIONS, options)
    let key = `${options.language}_${options.country}`

    // Return cached data if available
    if (marketProducts[key]) return marketProducts[key]

    // No cached data and no cached request make new server request
    let url = `/apps/global/services/featured/products?market=${options.country}&language=${options.language}`
    return mySiteHttp({ url })
}

/**
 * Returns a copy of the given object, with any undefined fields removed.
 * This is done for compatibility with the Firebase JavaScript API.
 * @param {*} obj - Object to clean
 * @returns {*} Cleaned copy of object
 * @memberof MySiteRestService
 * @private
 */
function toCleanedObj(obj) {
    if (obj instanceof Array) {
        return obj.map(field => toCleanedObj(field))
    } else if (obj instanceof Object) {
        let copy = Object.assign({}, obj)
        for (let key of Object.keys(copy)) {
            if (copy[key] === undefined) delete copy[key]
            else copy[key] = toCleanedObj(copy[key])
        }
        return copy
    } else {
        return obj
    }
}

function getAPIPath() {
    let isProd = false
    let prodUrls = [
        'author.nuskin.com',
        'www.nuskin.com',
        'mysite.mynuskin.com'
    ]
    if (window && window.location && window.location.host) {
        isProd = prodUrls.includes(window.location.host) || !window.location.host.includes('test')
    }
    return `https://${isProd ? '' : 'test'}api.cloud.nuskin.com/ns-share-api`
}

/**
 * axios wrapper
 * @param {Object} config - required param
 * @param {String} param.method - request method
 * @param {String} param.url - request url
 * @param {String} param.headers - request headers
 * @param {String} param.payload - request payload
 */
async function mySiteHttp(config) {
    user = UserService.getUser()
    config.method = config.method || 'get';
    config.headers = {
        'client_id': '735b1eb810304bba966af0891ab54053',
        'Content-Type': 'application/json'
    }
    if (user && user.eid) {
        config.headers['Authorization'] = `JWT ${user.eid}`
    }

    try {
        const response = await http.request(config);
        return response.data
    } catch (error) {
        const errConfig = error.config || { method: 'unknown', url: 'unknown' }
        const message = `[${errConfig.method.toUpperCase()}] call to '${errConfig.url}' failed in mySiteRestService.js: ${error.message} `
        return MySiteError.handle(error, message)
    }
}
